// Tarjeta de una acción propuesta por el asistente: vista previa con el efecto exacto y los botones
// para que la persona decida. El asistente no confirma nada: solo estos botones (con la sesión de la
// persona) ejecutan, cancelan o deshacen.
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Collapse,
  HStack,
  Input,
  Link,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { FaExclamationTriangle, FaFilePdf } from 'react-icons/fa';
import { cancelarAccion, confirmarAccion, deshacerAccion, editarAccion } from './asistenteApi';
import { downloadFile } from '../../../_Utils/mobileBridge';
import { getStoredAuthHeaders } from '../../../_Utils/authHeaders';

const ESTADOS = {
  propuesta: { texto: 'Para confirmar', color: 'blue' },
  ejecutando: { texto: 'Ejecutando', color: 'blue' },
  ejecutada: { texto: 'Hecho', color: 'green' },
  fallida: { texto: 'No se pudo', color: 'red' },
  cancelada: { texto: 'Cancelada', color: 'gray' },
  vencida: { texto: 'Vencida', color: 'gray' },
  deshecha: { texto: 'Deshecha', color: 'orange' },
};

const restante = (expira) => {
  const ms = new Date(expira).getTime() - Date.now();
  if (!(ms > 0)) return null;
  const min = Math.floor(ms / 60000);
  const seg = Math.floor((ms % 60000) / 1000);
  return `${min}:${String(seg).padStart(2, '0')}`;
};

const VistaPrevia = ({ datos }) => {
  if (!datos || typeof datos !== 'object') return null;
  const conocidas = ['resumen', 'lineas', 'tablas', 'avisos', 'alertas'];
  // Las vistas previas reales traen resumen/lineas/tablas/avisos y además valores técnicos (total, neto,
  // retenciones): esos no se muestran. Solo una vista previa sin ese formato se muestra clave por clave.
  const conFormato = conocidas.some((k) => k in datos);
  const sueltas = conFormato ? [] : Object.entries(datos).filter(([k]) => !conocidas.includes(k));
  return (
    <VStack align="stretch" spacing={2} fontSize="sm">
      {datos.resumen && <Text>{datos.resumen}</Text>}
      {(datos.lineas || []).map((l) => (
        <HStack key={l.etiqueta} justify="space-between" align="flex-start">
          <Text color="gray.600">{l.etiqueta}</Text>
          <Text fontWeight="semibold" textAlign="right" color={l.alerta ? 'red.600' : undefined}>
            {l.valor}
          </Text>
        </HStack>
      ))}
      {sueltas.map(([k, v]) => (
        <HStack key={k} justify="space-between">
          <Text color="gray.600">{k}</Text>
          <Text fontWeight="semibold">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</Text>
        </HStack>
      ))}
      {(datos.tablas || []).map((t) => (
        <Box key={t.titulo} overflowX="auto">
          <Text fontSize="xs" fontWeight="semibold" mb={1}>
            {t.titulo}
          </Text>
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                {t.columnas.map((c) => (
                  <Th key={c} px={2}>
                    {c}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {t.filas.map((f, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Tr key={i}>
                  {f.map((v, j) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Td key={j} px={2}>
                      {v}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      ))}
      {/* Advertencias que no frenan pero hay que mirar antes de enviar (p. ej. errores de la revisión del informe). */}
      {datos.alertas?.items?.length > 0 && (
        <Box borderWidth="1px" borderColor="red.300" bg="red.50" color="red.700" rounded="md" p={2}
          data-testid="accion-alertas">
          <Text fontSize="xs" fontWeight="bold" mb={1}>
            {datos.alertas.titulo || 'Revisar antes de enviar'}
          </Text>
          {datos.alertas.items.map((a) => (
            <HStack key={a} align="flex-start" spacing={2} fontSize="xs">
              <Box pt="2px">
                <FaExclamationTriangle />
              </Box>
              <Text>{a}</Text>
            </HStack>
          ))}
        </Box>
      )}
      {(datos.avisos || []).map((a) => (
        <HStack key={a} align="flex-start" spacing={2} color="orange.600" fontSize="xs">
          <Box pt="2px">
            <FaExclamationTriangle />
          </Box>
          <Text>{a}</Text>
        </HStack>
      ))}
    </VStack>
  );
};

// Filas para elegir (acciones con selección): casilla en las que se pueden confirmar; las bloqueadas
// muestran el motivo y las que tienen avisos vienen destildadas.
// Celdas editables de una fila: una columna (editable.columna: el importe, clave = id de la fila) o varias
// (editable.columnas: [{columna, campo}], clave = "id:campo"; liquidación por MANUAL).
export const celdasEditables = (datos) => {
  const e = datos?.editable;
  if (!e) return [];
  if (Array.isArray(e.columnas)) return e.columnas;
  return [{ columna: e.columna, campo: null }];
};
// Con editable.facturas (MANUAL), cada fila lista sus facturas impagas con casilla; las tildadas viajan como
// "id:facturas" = gasto_id separados por coma, igual que las celdas editables.
const camposEditables = (datos) =>
  [...celdasEditables(datos), ...(datos?.editable?.facturas ? [{ columna: null, campo: 'facturas' }] : [])];
const claveImporte = (fila, campo) => (campo ? `${fila.id}:${campo}` : fila.id);
const valorInicial = (fila, campo) => (campo ? fila.editables?.[campo] ?? '' : fila.importe ?? '');

// Con `datos.editable` (registrar cobranzas), la columna `editable.columna` es un importe por fila: se edita y, al
// salir del campo, el backend vuelve a preparar la propuesta (onEditar).
const tildadasDe = (importes, fila) =>
  String(importes[claveImporte(fila, 'facturas')] ?? '').split(',').filter(Boolean).map(Number);

// Botones de PDF (con login): los del resultado o la vista previa de la propuesta.
const Documentos = ({ docs, onError }) => {
  if (!docs?.length) return null;
  return (
    <HStack mt={2} spacing={3} wrap="wrap">
      {docs.map((d) => (
        <Button key={d.url} size="sm" variant="outline" colorScheme="blue" leftIcon={<FaFilePdf />}
          data-testid="accion-documento"
          onClick={() => Promise.resolve(downloadFile({
            url: `${process.env.REACT_APP_API_URL || ''}${d.url}`,
            filename: d.etiqueta ? `${d.etiqueta.replace(/[^\w° -]+/g, '').trim()}.pdf` : 'documento.pdf',
            headers: getStoredAuthHeaders() || {},
          })).catch(() => onError('No se pudo descargar el PDF. Probá desde la pantalla.'))}>
          {d.etiqueta}
        </Button>
      ))}
    </HStack>
  );
};

const SeccionPlan = ({ sec, ...props }) => {
  const [abierta, setAbierta] = useState(!sec.plegada);
  const tildadas = (sec.filas || []).filter((f) => props.elegidos.includes(f.id)).length;
  return (
    <Box mt={3} data-testid="accion-seccion">
      <HStack justify="space-between">
        <Text fontSize="sm" fontWeight="semibold">{sec.titulo}</Text>
        {sec.plegada && (
          <Button size="xs" variant="link" onClick={() => setAbierta((v) => !v)}>
            {abierta ? 'Ocultar' : `Ver (${tildadas} tildadas)`}
          </Button>
        )}
      </HStack>
      {abierta && <Seleccion datos={{ ...sec, etiqueta: 'fila' }} {...props} />}
    </Box>
  );
};

const Seleccion = ({ datos, elegidos, alternar, habilitada, importes, setImporte, onEditar, alternarFactura }) => (
  <Box overflowX="auto" mt={2}>
    <Table size="sm" variant="simple">
      <Thead>
        <Tr>
          <Th px={1} />
          {datos.columnas.map((c) => (
            <Th key={c} px={2}>
              {c}
            </Th>
          ))}
        </Tr>
      </Thead>
      <Tbody>
        {datos.filas.map((f) => (
          <React.Fragment key={f.id}>
            <Tr>
              <Td px={1}>
                {datos.casillas !== false && f.seleccionable && (
                  <Checkbox
                    aria-label={`Elegir ${(datos.etiqueta === 'fila' ? f.celdas.slice(0, 2).join(' ') : f.celdas[1]) || f.id}`}
                    isChecked={elegidos.includes(f.id)}
                    isDisabled={!habilitada}
                    onChange={() => alternar(f.id)}
                  />
                )}
              </Td>
              {f.celdas.map((v, j) => (
                // eslint-disable-next-line react/no-array-index-key
                <Td key={j} px={2} color={f.seleccionable || datos.casillas === false ? undefined : 'gray.400'}>
                  {(() => {
                    const ed = celdasEditables(datos).find((c) => c.columna === j);
                    if (!ed) return v;
                    const clave = claveImporte(f, ed.campo);
                    return (
                      <Input
                        size="xs"
                        w="110px"
                        inputMode="decimal"
                        aria-label={ed.campo ? `${datos.columnas[j]} ${f.celdas[0] || f.id}` : `Importe ${f.celdas[0] || f.id}`}
                        value={importes[clave] ?? ''}
                        isDisabled={!habilitada}
                        onChange={(e) => setImporte(clave, e.target.value)}
                        onBlur={onEditar}
                        onKeyDown={(e) => { if (e.key === 'Enter') onEditar(); }}
                      />
                    );
                  })()}
                </Td>
              ))}
            </Tr>
            {(f.bloqueo || (f.avisos || []).length > 0 || (f.notas || []).length > 0
              || (datos.editable?.facturas && (f.facturas || []).length > 0)) && (
              <Tr>
                <Td px={1} />
                <Td colSpan={datos.columnas.length} px={2} pt={0} borderTop="none">
                  {f.bloqueo && (
                    <Text fontSize="xs" color="red.500">
                      {f.bloqueo}
                    </Text>
                  )}
                  {(f.avisos || []).map((a) => (
                    <Text key={a} fontSize="xs" color="orange.600">
                      {a}
                    </Text>
                  ))}
                  {(f.notas || []).map((n) => (
                    <Text key={n} fontSize="xs" color="gray.500">
                      {n}
                    </Text>
                  ))}
                  {datos.editable?.facturas && (f.facturas || []).map((fac) => (
                    <Checkbox key={fac.gasto_id} size="sm" display="flex" isDisabled={!habilitada}
                      isChecked={tildadasDe(importes, f).includes(fac.gasto_id)}
                      onChange={() => alternarFactura(f, fac.gasto_id)}>
                      <Text as="span" fontSize="xs" color={fac.nueva ? 'blue.600' : 'gray.600'}
                        fontWeight={fac.nueva ? 'semibold' : 'normal'}>
                        {fac.texto}
                      </Text>
                    </Checkbox>
                  ))}
                </Td>
              </Tr>
            )}
          </React.Fragment>
        ))}
      </Tbody>
    </Table>
  </Box>
);

const AccionCard = ({ accion, onCambio }) => {
  const [ocupado, setOcupado] = useState(null);
  const [mensaje, setMensaje] = useState('');
  const [, setTic] = useState(0);
  const [verPropuesta, setVerPropuesta] = useState(false);
  const seleccion = accion.vista_previa?.seleccion || null;
  const [elegidos, setElegidos] = useState(() =>
    (seleccion?.filas || []).filter((f) => f.seleccionable && f.preseleccionada).map((f) => f.id)
  );
  const alternar = (id) => setElegidos((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const editable = Boolean(seleccion?.editable);
  const importesDe = (filas) => Object.fromEntries((filas || []).flatMap((f) =>
    camposEditables(seleccion).map((ed) => [claveImporte(f, ed.campo), valorInicial(f, ed.campo)])));
  const [importes, setImportes] = useState(() => importesDe(seleccion?.filas));
  // Cada vez que el backend vuelve a preparar la propuesta, la tabla toma sus importes y su selección.
  useEffect(() => {
    if (!editable) return;
    setImportes(importesDe(seleccion?.filas));
    setElegidos((seleccion?.filas || []).filter((f) => f.seleccionable && f.preseleccionada).map((f) => f.id));
  }, [accion.vista_previa]);
  const setImporte = (id, valor) => setImportes((prev) => ({ ...prev, [id]: valor }));
  const importesCambiados = (nuevos) =>
    (seleccion?.filas || []).some((f) => camposEditables(seleccion).some((ed) =>
      String(nuevos[claveImporte(f, ed.campo)] ?? '') !== String(valorInicial(f, ed.campo))));
  const noConfirmable = accion.vista_previa?.no_confirmable || null;
  const borde = useColorModeValue('blue.200', 'blue.700');
  const fondo = useColorModeValue('blue.50', 'whiteAlpha.100');
  const pendiente = accion.estado === 'propuesta';
  const tiempo = pendiente ? restante(accion.expira_en) : null;
  const clave = pendiente && !tiempo ? 'vencida' : accion.estado;
  const estado = ESTADOS[clave] || { texto: accion.estado, color: 'gray' };

  useEffect(() => {
    if (!pendiente) return undefined;
    const t = setInterval(() => setTic((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [pendiente]);

  const paso = (fn, nombre, extra) => async () => {
    setOcupado(nombre);
    setMensaje('');
    try {
      const nueva = extra === undefined ? await fn(accion.id) : await fn(accion.id, extra);
      if (onCambio) onCambio(nueva);
    } catch (err) {
      const datos = err?.response?.data || {};
      setMensaje(datos.detail || 'No se pudo completar. Probá de nuevo.');
      if (datos.accion && onCambio) onCambio(datos.accion);
    } finally {
      setOcupado(null);
    }
  };

  const editar = async (nuevos = importes) => {
    if (!importesCambiados(nuevos) || ocupado) return;
    setOcupado('editar');
    setMensaje('');
    try {
      const nueva = await editarAccion(accion.id, nuevos);
      if (onCambio) onCambio(nueva);
    } catch (err) {
      const datos = err?.response?.data || {};
      setMensaje(datos.detail || 'No se pudo actualizar la propuesta. Probá de nuevo.');
      if (datos.accion && onCambio) onCambio(datos.accion);
    } finally {
      setOcupado(null);
    }
  };
  // "Completar con lo adeudado" (cobranzas) o "Usar los gastos de este mes" (liquidación): el backend dice el texto y
  // de qué campo de cada fila sale el importe (editable.completar).
  const completar = seleccion?.editable?.completar || { etiqueta: 'Completar con lo adeudado', campo: 'a_pagar_hoy' };
  const sinCasillas = seleccion?.casillas === false;
  const completarConLoAdeudado = () => {
    const nuevos = Object.fromEntries((seleccion?.filas || []).map((f) => [f.id, f[completar.campo] ?? '']));
    setImportes(nuevos);
    editar(nuevos);
  };
  // Tildar o destildar una factura vuelve a preparar la propuesta (los pendientes los suma el backend).
  const alternarFactura = (fila, gastoId) => {
    const actuales = tildadasDe(importes, fila);
    const tildadas = actuales.includes(gastoId) ? actuales.filter((g) => g !== gastoId) : [...actuales, gastoId];
    const nuevos = { ...importes, [claveImporte(fila, 'facturas')]: tildadas.sort((a, b) => a - b).join(',') };
    setImportes(nuevos);
    editar(nuevos);
  };
  const extraConfirmar = !seleccion || sinCasillas
    ? undefined
    : [...elegidos].sort((a, b) => a - b);

  return (
    <Box borderWidth="1px" borderColor={borde} bg={fondo} borderRadius="md" p={3} data-testid="accion-card">
      <HStack justify="space-between" mb={2} align="flex-start">
        <Box>
          <Text fontSize="xs" color="gray.500">
            {accion.tipo_titulo}
          </Text>
          <Text fontWeight="semibold" fontSize="sm">
            {accion.titulo}
          </Text>
        </Box>
        <Badge colorScheme={estado.color}>{estado.texto}</Badge>
      </HStack>
      {pendiente ? (
        <>
          <VistaPrevia datos={accion.vista_previa} />
          {/* Vista previa (p. ej. el PDF del informe con marca de agua), con login y mientras la propuesta esté abierta. */}
          <Documentos docs={accion.documentos_propuesta} onError={setMensaje} />
          {editable && (seleccion.editable.completar || !Array.isArray(seleccion.editable.columnas)) && (
            <Button size="xs" variant="outline" mt={2} onClick={completarConLoAdeudado}
              isDisabled={Boolean(ocupado)} isLoading={ocupado === 'editar'}>
              {completar.etiqueta}
            </Button>
          )}
          {seleccion && !seleccion.secciones && (
            <Seleccion datos={seleccion} elegidos={elegidos} alternar={alternar} habilitada={!ocupado}
              importes={importes} setImporte={setImporte} onEditar={() => editar()}
              alternarFactura={alternarFactura} />
          )}
          {/* Plan de conciliación: la misma selección, por secciones (cada una con sus columnas); las casillas de
              todas van en la misma lista de elegidos. Las secciones largas arrancan plegadas. */}
          {seleccion?.secciones && seleccion.secciones.map((sec) => (
            <SeccionPlan key={sec.titulo} sec={sec} elegidos={elegidos} alternar={alternar} habilitada={!ocupado}
              importes={importes} setImporte={setImporte} onEditar={() => editar()}
              alternarFactura={alternarFactura} />
          ))}
        </>
      ) : (
        <>
          {/* Ya no está para confirmar: primero lo que pasó; la propuesta queda plegada. */}
          {accion.resultado_texto && (
            <Text fontSize="sm" fontWeight="semibold" color={accion.estado === 'fallida' ? 'red.500' : undefined}>
              {accion.resultado_texto}
            </Text>
          )}
          {accion.estado === 'ejecutada' &&
            (accion.resultado?.lineas?.length > 0 || accion.resultado?.tablas?.length > 0) && (
            <Box mt={2} data-testid="accion-resultado">
              <VistaPrevia datos={{ lineas: accion.resultado.lineas, tablas: accion.resultado.tablas }} />
            </Box>
          )}
          {accion.estado === 'ejecutada' && (
            <Documentos docs={accion.resultado?.documentos} onError={setMensaje} />
          )}
          <Button size="xs" variant="link" mt={2} onClick={() => setVerPropuesta((v) => !v)}>
            {verPropuesta ? 'Ocultar lo que se propuso' : 'Ver lo que se propuso'}
          </Button>
          <Collapse in={verPropuesta} animateOpacity>
            <Box mt={2} opacity={0.85}>
              <VistaPrevia datos={accion.vista_previa} />
            </Box>
          </Collapse>
        </>
      )}
      {mensaje && (
        <Text mt={2} fontSize="sm" color="orange.600">
          {mensaje}
        </Text>
      )}
      {pendiente && tiempo && (
        <HStack mt={3} spacing={2}>
          <Button size="sm" colorScheme="blue"
            onClick={editable
              ? paso((id) => confirmarAccion(id, extraConfirmar, importes), 'confirmar')
              : paso(confirmarAccion, 'confirmar', extraConfirmar)}
            isLoading={ocupado === 'confirmar'}
            isDisabled={Boolean(ocupado) || (seleccion && !sinCasillas && !elegidos.length)
              || (editable && importesCambiados(importes)) || Boolean(noConfirmable)}>
            {seleccion && !sinCasillas ? `Confirmar (${elegidos.length})` : 'Confirmar'}
          </Button>
          <Button size="sm" variant="ghost" onClick={paso(cancelarAccion, 'cancelar')}
            isLoading={ocupado === 'cancelar'} isDisabled={Boolean(ocupado)}>
            Cancelar
          </Button>
          <Text fontSize="xs" color="gray.500">
            Vence en {tiempo}
          </Text>
        </HStack>
      )}
      {accion.estado === 'ejecutada' && (
        <VStack align="stretch" mt={3} spacing={1}>
          <HStack spacing={3}>
            {accion.enlace && (
              <Link as={RouterLink} to={accion.enlace} fontSize="sm" color="blue.600" fontWeight="semibold">
                Continuar en la pantalla
              </Link>
            )}
            {accion.puede_deshacer && (
              <Button size="xs" variant="outline" onClick={paso(deshacerAccion, 'deshacer')}
                isLoading={ocupado === 'deshacer'}>
                Deshacer
              </Button>
            )}
          </HStack>
          {!accion.puede_deshacer && accion.motivo_no_deshacer && (
            <Text fontSize="xs" color="gray.500">
              {accion.motivo_no_deshacer}
            </Text>
          )}
        </VStack>
      )}
    </Box>
  );
};

export default AccionCard;
